import React, { useState, useEffect, useRef } from "react";
import { captureSubmissionError } from "../../utils/sentryUtils";
import { TERMS_AND_CONDITIONS_VERSION } from "../../constants";
import SubmittingCard from "../../components/SubmittingCard";
import SubmissionErrorCard from "../../components/SubmissionErrorCard";

const RemoSubmission = ({ remoSignupFlowState: flowState, onFlowComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Prevents duplicate remortgage submissions caused by React 18 StrictMode
  // double-mounting effects in development. React docs recommend cleanup over
  // ref guards, but cleanup (abort) is wrong here because the PUT is not
  // idempotent and must run to completion so onFlowComplete() fires. The ref
  // persists across the StrictMode unmount-remount cycle, so the second mount
  // sees hasInitiated=true and skips the fetch. No abort cleanup is provided;
  // if the component unmounts mid-flight (e.g. browser back), the orphaned
  // .then() handlers will attempt state updates that React 18 silently ignores.
  const hasInitiated = useRef(false);

  useEffect(() => {
    if (hasInitiated.current) return;
    hasInitiated.current = true;

    let numberOfOwners = flowState.otherOwners ? flowState.otherOwners.numberOfOwners : 0;
    let otherOwners = [];
    if (numberOfOwners === 4) {
      otherOwners = [flowState.otherOwners.client2, flowState.otherOwners.client3, flowState.otherOwners.client4];
    } else if (numberOfOwners === 3) {
      otherOwners = [flowState.otherOwners.client2, flowState.otherOwners.client3];
    } else if (numberOfOwners === 2) {
      otherOwners = [flowState.otherOwners.client2];
    } else {
      otherOwners = [];
    }

    let numberOfOccupiers = flowState.otherOccupiers ? flowState.otherOccupiers.numberOfOccupiers : 0;
    let otherOccupiers = [];
    if (numberOfOccupiers === 3) {
      otherOccupiers = [
        flowState.otherOccupiers.occupier1,
        flowState.otherOccupiers.occupier2,
        flowState.otherOccupiers.occupier3,
      ];
    } else if (numberOfOccupiers === 2) {
      otherOccupiers = [flowState.otherOccupiers.occupier1, flowState.otherOccupiers.occupier2];
    } else if (numberOfOccupiers === 1) {
      otherOccupiers = [flowState.otherOccupiers.occupier1];
    } else {
      otherOccupiers = [];
    }

    let areThereAnyAdultOccupiers = numberOfOccupiers > 0;

    const body = {
      leadId: flowState.leadId,
      transactionType: flowState.transactionType,
      termsAndConditionsVersion: TERMS_AND_CONDITIONS_VERSION,
      remortgagePropertyAddress: flowState.propertyAddress,
      primaryClient: {
        ...flowState.yourName.client1,
        ...flowState.contactDetails,
      },
      remortgagingAddressDetails: {
        isBuyToLet: flowState.propertyInformation1.isBuyToLet,
        isTransferOfEquity: flowState.propertyInformation1.isTransferOfEquity,
        hasClientHadDisputesWithNeighbours: flowState.propertyInformation1.hasHadDisputesWithNeighbours,
        informationAboutDisputesWithNeighbours: flowState.propertyInformation1.disputesWithNeighboursInfo,
        paymentDemandedForLocalChurchRepairs: flowState.propertyInformation1.hasHadPaymentDemandsForChurchRepairs,
        informationAboutChurchRepairs: flowState.propertyInformation1.churchRepairsInfo,
        hasClientReceivedNotices: flowState.propertyInformation2.hasClientReceivedNotices,
        informationAboutNotices: flowState.propertyInformation2.noticesInfo,
        isPropertyConnectedToStandardUtilities: flowState.propertyInformation2.isPropertyConnectedToUtilities,
        informationAboutConnectionToStandardUtilities: flowState.propertyInformation2.connectionToUtilitiesInfo,
        numberOfManagementCompanies: flowState.propertyInformation2.numberOfManagementCompanies,
        whoInsuresProperty: flowState.propertyInformation2.whoInsuresProperty,
        doesTheClientPayAnyMaintenanceFees: flowState.propertyInformation2.clientPaysMaintenanceFees,
        informationAboutMaintenanceFees: flowState.propertyInformation2.maintenanceFeesInfo,
        haveThereBeenAnyRecentBuildingWorks: flowState.propertyInformation2.hasBuildingWorks,
        informationAboutBuildingWorks: flowState.propertyInformation2.buildingWorksInfo,
        isTheClientPayingOffSomeOfTheirExistingLoan: flowState.propertyInformation3.clientIsPayingOffSomeOfLoan,
        propertyIsTenanted: flowState.propertyInformation1.rejectHasTenants,
      },
      remortgageMortgageLender: flowState.mortgageLender,
      remortgageMortgageBroker: flowState.mortgageBroker,
      otherOwners,
      areThereAnyAdultOccupiers,
      otherOccupiers,
      completionDetails: flowState.completionDate,
      anythingElse: flowState.anythingElse,
    };

    fetch("/api/signup", {
      method: "PUT",
      body: JSON.stringify(body),
    }).then(
      async (resp) => {
        if (resp.ok) {
          setLoading(false);
          onFlowComplete();
        } else {
          const body = await resp.text().catch(() => "");
          console.error("Remo submission failed:", resp.status, body);
          captureSubmissionError("RemortgageSignup", resp.status, body);
          setError("Unable to process your signup");
          setLoading(false);
        }
      },
      (err) => {
        console.error("Remo submission failed:", err);
        setError("Unable to process your signup");
        setLoading(false);
      },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fires once on mount; ref guard prevents StrictMode re-fire; props are stable for component lifetime

  if (loading) {
    return <SubmittingCard />;
  }

  if (error) {
    return <SubmissionErrorCard error={error} />;
  }

  return null;
};

export default RemoSubmission;
